import { useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import SearchIcon from '@mui/icons-material/Search';
import { IconButton, InputAdornment, TextField, TextFieldProps } from '@mui/material';
import { fCurrencyKRW, unformatNumber } from 'src/utils/formatNumber';

import Iconify from '../iconify';

type Props = TextFieldProps & {
  name: string;
  readonly?: boolean;
  allowZero?: boolean;
};

export default function RHFTextField({
  name,
  helperText,
  type,
  InputProps,
  readonly,
  allowZero = false,
  ...other
}: Props) {
  const { control } = useFormContext();
  const [showPassword, setShowPassword] = useState(false);

  const handleTogglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };
  // input value를 처리하는 함수
  const getInputValue = (value: any) => {
    if (typeof value === 'number') {
      if (value === 0) {
        return allowZero ? '0' : '';
      }
      if (type === 'amount') {
        return fCurrencyKRW(value);
      }
    }
    return value ?? '';
  };

  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { ref, ...field }, fieldState: { error } }) => {
        return (
          <TextField
            {...field}
            inputRef={ref}
            name={name}
            fullWidth
            size={'small'}
            type={type === 'password' && showPassword ? 'text' : type}
            value={getInputValue(field.value)}
            error={!!error}
            helperText={error ? error?.message : helperText}
            InputProps={{
              // inputProps로 추가하되 type이 password일 경우 endAdornment로 eye icon+기능 추가
              ...InputProps,
              inputProps: {
                readOnly: readonly ?? false,
              },
              // password: 눈 아이콘 추가
              ...(type === 'password' && {
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={handleTogglePasswordVisibility} edge="end">
                      <Iconify icon={showPassword ? 'eva:eye-fill' : 'eva:eye-off-fill'} />
                    </IconButton>
                  </InputAdornment>
                ),
              }),
              // search: 검색 아이콘 추가
              ...(type === 'search' && {
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton edge="end">
                      <SearchIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }),
            }}
            // onChange 시 숫자 형식을 원래 값으로 변환 후 onChange 호출
            onChange={(e) => {
              const inputValue = e.target.value;
              if (type === 'amount') {
                const numericValue = Number(unformatNumber(inputValue)); // 쉼표 제거 후 숫자로 변환
                field.onChange(numericValue);
              } else {
                field.onChange(inputValue);
              }
            }}
            {...other}
          />
        );
      }}
    />
  );
}
